/**
 * Color Preferences Service
 * Manages user's custom calendar color preferences using localStorage AND API persistence
 */
import { user as userApi } from './api';

const STORAGE_KEY = 'calendar_custom_colors';

/**
 * Get all custom colors from localStorage
 * @returns {Object} Map of turnoId -> color
 */
export const getCustomColors = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch (e) {
        console.warn('Error reading custom colors:', e);
        return {};
    }
};

/**
 * Sync from existing preferences object (avoid double API call)
 */
export const syncFromPrefs = (prefs) => {
    try {
        if (prefs && prefs.calendar_colors) {
            const serverColors = prefs.calendar_colors;
            if (typeof serverColors === 'object') {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(serverColors));
                window.dispatchEvent(new Event('calendarColorsChanged'));
                console.log('🎨 Calendar colors synced from cloud');
            }
        }
    } catch (e) {
        console.warn('Error applying cloud colors:', e);
    }
};

/**
 * Sync local preferences with API (Download from cloud)
 * Should be called on app init/login
 */
export const syncWithApi = async () => {
    try {
        const prefs = await userApi.getPreferences();
        syncFromPrefs(prefs);
    } catch (e) {
        // Silent fail on network error, keep local
        console.warn('Skipped color sync:', e.message);
    }
};

/**
 * Set a custom color for a specific turno
 * @param {string} turnoName - Name of the turno (e.g., 'Mañana', 'Tarde')
 * @param {string} color - Hex color value
 */
export const setCustomColor = async (turnoName, color) => {
    try {
        // 1. Optimistic update (Local)
        const current = getCustomColors();
        current[turnoName] = color;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
        window.dispatchEvent(new Event('calendarColorsChanged'));

        // 2. Persist to Cloud (API)
        await userApi.updatePreferences({ calendar_colors: current });
    } catch (e) {
        console.warn('Error saving custom color:', e);
    }
};

/**
 * Reset all custom colors (revert to database defaults)
 */
export const resetColors = async () => {
    try {
        // 1. Local clear
        localStorage.removeItem(STORAGE_KEY);
        window.dispatchEvent(new Event('calendarColorsChanged'));

        // 2. Cloud clear
        await userApi.updatePreferences({ calendar_colors: {} });
    } catch (e) {
        console.warn('Error resetting colors:', e);
    }
};

/**
 * Check if user has any custom colors set
 * @returns {boolean}
 */
export const hasCustomColors = () => {
    return Object.keys(getCustomColors()).length > 0;
};

/**
 * Apply custom colors to a turnos array from the database
 * Returns a new array with custom colors merged in
 * @param {Array} turnos - Original turnos array from API
 * @returns {Array} Turnos with custom colors applied
 */
export const applyCustomColors = (turnos) => {
    const customColors = getCustomColors();
    if (Object.keys(customColors).length === 0) {
        return turnos;
    }

    return turnos.map(turno => {
        const customColor = customColors[turno.nombre];
        if (customColor) {
            return {
                ...turno,
                color_bg: customColor,
                // Keep original color_text or compute based on luminance
                color_text: turno.color_text
            };
        }
        return turno;
    });
};

export default {
    getCustomColors,
    setCustomColor,
    resetColors,
    hasCustomColors,
    applyCustomColors,
    syncWithApi,
    syncFromPrefs
};
